// 🆕 19.10.2026 · Auftrag 57 (Nachtrag 1): Klickstrecke Bodenklicks an Ankerbildern.
// Nur hinter REACT_APP_BODENKLICK_STRECKE=1 eingehängt (Wizard.jsx). Zeigt das Ankerbild (Frame zur Ankerzeit),
// Klick setzt einen Bodenpunkt (Füße), Rechtsklick auf einen Punkt nimmt ihn weg; 2–4 Punkte, höchstens einer
// „im Lauf“. Regeln stehen in lib/bodenklicks.js, Strecke, Rückhalt und Sperre im Backend (routes/bodenklicks.py).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  exportBodenklicks, frameUrl, getBodenklickStrecke, loescheBodenklicks, setzeBodenklickHalbzeit, speichereBodenklicks,
} from "@/lib/api";
import {
  ANLEITUNG_ZEILEN, MODUS_PRUEF, MODUS_STRECKE, fortschrittText, imLaufUmschalten, klickKoerper, naechsterOffener,
  punktHinzu, punktWeg, punkteFehler, punkteZaehlerText, vorhandenePunkte, zuBildPixel, zweckText,
} from "@/lib/bodenklicks";

export default function BodenklickStrecke({ sequenceId }) {
  const [daten, setDaten] = useState(null);
  const [modus, setModus] = useState(MODUS_STRECKE);
  const [aktuell, setAktuell] = useState(null);
  const [punkte, setPunkte] = useState([]);
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const [grenze, setGrenze] = useState("");
  const imgRef = useRef(null);

  const laden = useCallback(async () => {
    const d = await getBodenklickStrecke(sequenceId);
    setDaten(d);
    return d;
  }, [sequenceId]);

  useEffect(() => {
    laden().then((d) => {
      const erste = naechsterOffener(d.lern, d.klicks, null) || d.lern[0]?.anker_id || null;
      setAktuell(erste);
      setPunkte(vorhandenePunkte(d.klicks, erste));
      setGrenze(d.halbzeit_grenze_s ?? "");
    }).catch(() => toast.error("Strecke nicht ladbar"));
  }, [laden]);

  const liste = useMemo(() => (daten ? daten[modus] : []), [daten, modus]);
  const punkt = useMemo(() => liste.find((p) => p.anker_id === aktuell) || null, [liste, aktuell]);

  const waehle = (ankerId) => {
    setAktuell(ankerId);
    setPunkte(vorhandenePunkte(daten?.klicks, ankerId));
  };

  const wechsleModus = (m) => {
    setModus(m);
    const l = daten ? daten[m] : [];
    waehle(naechsterOffener(l, daten?.klicks, null) || l[0]?.anker_id || null);
  };

  const klick = (e) => {
    const p = zuBildPixel(e.clientX, e.clientY, imgRef.current?.getBoundingClientRect(), nat.w, nat.h);
    setPunkte((alt) => punktHinzu(alt, p));
  };

  const speichern = async (weiter) => {
    const f = punkteFehler(punkte);
    if (f) { toast.warning(f); return; }
    try {
      await speichereBodenklicks(sequenceId, aktuell, klickKoerper(punkte, nat.w, nat.h));
      const d = await laden();
      toast.success("Bodenklicks gespeichert");
      if (weiter) {
        const n = naechsterOffener(d[modus], d.klicks, aktuell);
        if (n) waehle(n); else toast.info("Alle Ankerbilder dieser Hälfte geklickt");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  const entfernen = async () => {
    await loescheBodenklicks(sequenceId, aktuell);
    setPunkte([]);
    await laden();
  };

  const grenzeSetzen = async () => {
    const g = grenze === "" ? null : Number(grenze);
    await setzeBodenklickHalbzeit(sequenceId, g);
    await laden();
  };

  const exportieren = async () => {
    const ex = await exportBodenklicks(sequenceId);
    const blob = new Blob([JSON.stringify(ex, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `bodenklicks_${sequenceId.slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!daten) return <div className="font-mono text-xs text-muted-foreground">Strecke lädt …</div>;

  return (
    <div data-testid="bodenklick-strecke" className="border border-amber-400/40 rounded-md p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3 font-mono text-[11px]">
        <Button size="sm" variant={modus === MODUS_STRECKE ? "default" : "outline"} onClick={() => wechsleModus(MODUS_STRECKE)}>
          Strecke (1. Halbzeit) · {fortschrittText(daten.lern, daten.klicks)}
        </Button>
        <Button size="sm" variant={modus === MODUS_PRUEF ? "default" : "outline"} onClick={() => wechsleModus(MODUS_PRUEF)}>
          Prüfhälfte (2. Halbzeit) · {fortschrittText(daten.pruef, daten.klicks)}
        </Button>
        <span className="text-muted-foreground">Halbzeitgrenze (s):</span>
        <input data-testid="bodenklick-grenze" className="w-24 bg-transparent border border-border px-1" value={grenze}
               onChange={(e) => setGrenze(e.target.value)} />
        <Button size="sm" variant="outline" onClick={grenzeSetzen}>setzen</Button>
        <Button size="sm" variant="outline" onClick={exportieren}>Export</Button>
      </div>
      {daten.hinweis && <div data-testid="bodenklick-hinweis" className="font-mono text-[11px] text-amber-300">{daten.hinweis}</div>}
      {daten.klasse_offen.length > 0 && (
        <div data-testid="bodenklick-klasse-offen" className="font-mono text-[11px] text-muted-foreground">
          Klasse offen (ohne Override, nicht in der Strecke):{" "}
          {daten.klasse_offen.map((k) => `Shot ${k.shot} (${k.anker} Anker)`).join(", ")}
        </div>
      )}
      {liste.length === 0 ? (
        <div className="font-mono text-xs text-muted-foreground">Keine Ankerbilder in dieser Hälfte.</div>
      ) : (
        <div className="flex gap-4">
          <div className="w-48 max-h-[480px] overflow-y-auto font-mono text-[11px] space-y-1">
            {liste.map((p) => {
              const fertig = daten.klicks.some((k) => k.anker_id === p.anker_id);
              return (
                <button key={p.anker_id} type="button" onClick={() => waehle(p.anker_id)}
                        className={`block w-full text-left px-2 py-1 rounded ${p.anker_id === aktuell ? "bg-amber-400/20" : ""}`}>
                  {fertig ? "✓" : "·"} {p.nr}. Shot {p.shot} · {p.t_s.toFixed(2)} s{p.zurueckgehalten ? " · zurückgehalten" : ""}
                </button>
              );
            })}
          </div>
          {punkt && (
            <div className="flex-1 space-y-2">
              <div className="font-mono text-[11px]">
                Ankerbild {punkt.nr} · Shot {punkt.shot} · t = {punkt.t_s.toFixed(3)} s · {punkt.img_ref} ·{" "}
                <span data-testid="bodenklick-zweck" className={punkt.zurueckgehalten || punkt.haelfte === MODUS_PRUEF ? "text-amber-300" : ""}>
                  {zweckText(punkt)}
                </span>
              </div>
              <div data-testid="bodenklick-anleitung" className="font-mono text-[11px] text-amber-200 border border-amber-400/30 rounded px-2 py-1">
                {ANLEITUNG_ZEILEN.map((z) => <div key={z}>{z}</div>)}
              </div>
              <div className="relative inline-block select-none">
                <img ref={imgRef} src={frameUrl(sequenceId, punkt.t_s)} alt="Ankerbild" draggable={false}
                     onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                     onClick={klick} onContextMenu={(e) => e.preventDefault()}
                     className="max-w-full cursor-crosshair" data-testid="bodenklick-bild" />
                {nat.w > 0 && punkte.map((p, i) => (
                  <div key={i} title="Rechtsklick: entfernen"
                       onContextMenu={(e) => { e.preventDefault(); setPunkte((alt) => punktWeg(alt, i)); }}
                       className={`absolute w-3 h-3 -ml-1.5 -mt-1.5 rounded-full border-2 ${p.im_lauf ? "border-red-400" : "border-amber-300"}`}
                       style={{ left: `${(p.u / nat.w) * 100}%`, top: `${(p.v / nat.h) * 100}%` }} />
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 font-mono text-[11px]">
                <span>{punkteZaehlerText(punkte.length)}</span>
                {punkte.map((p, i) => (
                  <Button key={i} size="sm" variant={p.im_lauf ? "default" : "outline"} onClick={() => setPunkte((alt) => imLaufUmschalten(alt, i))}>
                    {i + 1}: {p.im_lauf ? "im Lauf" : "steht"}
                  </Button>
                ))}
                <Button size="sm" data-testid="bodenklick-speichern" onClick={() => speichern(false)}>Speichern</Button>
                <Button size="sm" data-testid="bodenklick-weiter" onClick={() => speichern(true)}>Speichern und weiter</Button>
                <Button size="sm" variant="outline" onClick={() => setPunkte([])}>Punkte leeren</Button>
                <Button size="sm" variant="outline" onClick={entfernen}>Gespeicherte entfernen</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
